import { useState, useCallback, useMemo } from 'react';
import {
  DamageClaimFormData,
  DamageType,
  PhysicalDamageSubcategory,
  StepId,
  COLLISION_FLOW,
  THEFT_WEATHER_FLOW,
  SOMETHING_ELSE_FLOW,
  MECHANICAL_FLOW,
} from '../types';

const createInitialFormData = (bookingId: string): DamageClaimFormData => ({
  bookingId,
  damageType: undefined,
  physicalDamageSubcategory: undefined,
  beforeEvidenceSlots: [],
  afterEvidenceSlots: [],
  policeReportFiled: undefined,
  waterInsideCar: undefined,
  whoWasDriving: undefined,
  anyoneInjured: undefined,
  anotherPersonInvolved: undefined,
  isCarDriveable: undefined,
  didVehicleLoseFluids: undefined,
  didAirbagsDeploy: undefined,
  incidentOccurredAt: undefined,
  incidentTime: undefined,
  incidentLocation: undefined,
  damagedParts: [],
  damagedPartsOtherDescription: undefined,
  mechanicalIssues: [],
  mechanicalIssueOtherDescription: undefined,
  damageDescription: undefined,
  additionalInfo: undefined,
  repairShopInfo: undefined,
  repairEstimateRange: undefined,
});

export const useDamageClaimForm = (bookingId: string) => {
  const [formData, setFormData] = useState<DamageClaimFormData>(createInitialFormData(bookingId));
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Determine current flow based on damage type and subcategory
  const currentFlow = useMemo((): StepId[] => {
    if (formData.damageType === DamageType.MECHANICAL) {
      return MECHANICAL_FLOW;
    }

    if (formData.damageType === DamageType.PHYSICAL) {
      switch (formData.physicalDamageSubcategory) {
        case PhysicalDamageSubcategory.COLLISION:
          return COLLISION_FLOW;
        case PhysicalDamageSubcategory.THEFT_VANDALISM:
        case PhysicalDamageSubcategory.WEATHER_NATURAL_DISASTER:
          return THEFT_WEATHER_FLOW;
        case PhysicalDamageSubcategory.SOMETHING_ELSE:
          return SOMETHING_ELSE_FLOW;
        default:
          // Still determining subcategory, show the beginning steps
          return COLLISION_FLOW.slice(0, 3);
      }
    }

    // Default flow (just instructions and damage type selection)
    return COLLISION_FLOW.slice(0, 2);
  }, [formData.damageType, formData.physicalDamageSubcategory]);

  const currentStepId = currentFlow[currentStepIndex] || 'instructions';
  const isLastStep = currentStepIndex === currentFlow.length - 1;
  const isFirstStep = currentStepIndex === 0;
  const totalSteps = currentFlow.length;
  const progress = ((currentStepIndex + 1) / totalSteps) * 100;

  const updateFormData = useCallback((updates: Partial<DamageClaimFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  const goToNextStep = useCallback(() => {
    if (currentStepIndex < currentFlow.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    }
  }, [currentStepIndex, currentFlow.length]);

  const goToPreviousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback(
    (stepId: StepId) => {
      const index = currentFlow.indexOf(stepId);
      if (index !== -1) {
        setCurrentStepIndex(index);
      }
    },
    [currentFlow],
  );

  const resetForm = useCallback(() => {
    setFormData(prev => {
      prev.beforeEvidenceSlots.forEach(s => {
        if (s.kind === 'upload') URL.revokeObjectURL(s.previewUrl);
      });
      prev.afterEvidenceSlots.forEach(s => {
        if (s.kind === 'upload') URL.revokeObjectURL(s.previewUrl);
      });
      return createInitialFormData(bookingId);
    });
    setCurrentStepIndex(0);
  }, [bookingId]);

  // Validate current step before moving to next
  const validateCurrentStep = useCallback((): boolean => {
    switch (currentStepId) {
      case 'instructions':
        return true;

      case 'damageType':
        return !!formData.damageType;

      case 'subcategory':
        return !!formData.physicalDamageSubcategory;

      case 'photos':
        // After photos are required
        return formData.afterEvidenceSlots.length > 0;

      case 'policeReport':
        return !!formData.policeReportFiled;

      case 'waterInsideCar':
        return !!formData.waterInsideCar;

      case 'isCarDriveable':
      case 'mechanicalIsCarDriveable':
        return !!formData.isCarDriveable;

      case 'didVehicleLoseFluids':
        return !!formData.didVehicleLoseFluids;

      case 'didAirbagsDeploy':
        return !!formData.didAirbagsDeploy;

      case 'anyoneInjured':
        return !!formData.anyoneInjured;

      case 'whoWasDriving':
        return !!formData.whoWasDriving;

      case 'anotherPersonInvolved':
        return !!formData.anotherPersonInvolved;

      case 'incidentDateTime':
        return !!formData.incidentOccurredAt;

      case 'incidentLocation':
        // Location is optional per the screenshots
        return true;

      case 'damagedParts':
        return formData.damagedParts.length > 0;

      case 'mechanicalIssues':
        return formData.mechanicalIssues.length > 0;

      case 'damageDescription':
      case 'mechanicalDamageDescription':
        return !!formData.damageDescription && formData.damageDescription.trim().length > 0;

      case 'repairShopInfo':
      case 'mechanicalRepairShopInfo':
        // Repair shop info is optional
        return true;

      case 'additionalInfo':
      case 'mechanicalAdditionalInfo':
        // Additional info is optional
        return true;

      case 'repairEstimate':
        return !!formData.repairEstimateRange;

      default:
        return true;
    }
  }, [currentStepId, formData]);

  return {
    formData,
    updateFormData,
    currentStepId,
    currentStepIndex,
    currentFlow,
    isLastStep,
    isFirstStep,
    totalSteps,
    progress,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    resetForm,
    validateCurrentStep,
  };
};
